import { checkPermissions, permissionPresets } from '@/utils'

/**
 * @typedef {object} RouteGuardStore
 * @property {Record<string, any>} getters
 * @property {(type: string, payload?: any, options?: object) => Promise<any>} dispatch
 */

/**
 * Создаёт навигационный guard роутера для переданного store.
 *
 * @param {RouteGuardStore} store
 * @returns {(to: object, from: object, next: Function) => void}
 */
export const createRouteGuard = store => {
  return (to, from, next) => {
    // Состояние авторизации фиксируется один раз: в пределах одной проверки оно неизменно.
    const hasToken = !!store.getters['authentication/token']
    const account = store.getters['account/account']

    // Флаги берутся из всей цепочки matched: meta может быть задана на родительском маршруте.
    const requiresAuth = to.matched.some(match => {
      return match.meta.auth
    })
    const requiresGuest = to.matched.some(match => {
      return match.meta.guest
    })

    // Если маршрут требует авторизации и токена нет, происходит переход на login.
    if (requiresAuth && !hasToken) {
      next({ name: 'login' })

      return
    }

    // Если маршрут предназначен для гостей и токен есть, происходит переход на home.
    if (requiresGuest && hasToken) {
      next({ name: 'home' })

      return
    }

    // Если у маршрута задан meta.permission и аккаунт загружен и у аккаунта есть id,
    // права сверяются с meta.permissionAccept или, когда он не задан, с resellerUp.
    // Иначе обработка продолжается ниже.
    if (to.meta.permission && account && account.id) {
      const allowed = checkPermissions(
        to.meta.permission,
        'or',
        to.meta.permissionAccept || permissionPresets.resellerUp,
        account.permissions
      )

      // Если права достаточны, происходит переход на запрошенный маршрут.
      if (allowed) {
        next()

        return
      }

      // Если права недостаточны, пользователю показывается уведомление об ошибке
      // и происходит переход на account.
      store
        .dispatch('notifications/addSnackbar', {
          text: 'Нет прав на просмотр данной страницы.',
          type: 'error'
        })
        .then(() => {
          next({ name: 'account' })
        })

      return
    }

    // Если у маршрута или у его родителя задан meta.handler, переход выполняет обработчик,
    // а его возврат true означает, что переход уже выполнен внутри него.
    const handled = to.matched.some(match => {
      if (match.meta.handler) {
        return match.meta.handler({
          store,
          to,
          from,
          next
        })
      }

      return false
    })

    // Если ни один обработчик не вернул true, происходит переход на запрошенный маршрут.
    if (!handled) {
      next()
    }
  }
}
